<template>
  <v-card
    flat
    class="vetalfa-card-grey-border pa-0 timeline-card"
    style="border-left: 4px solid #f44336; overflow: hidden"
  >
    <v-row class="ma-0 pa-0" align="center" no-gutters>
      <v-col cols="auto" class="pa-3 d-flex align-center vetalfa-pointer" @click="open">
        <v-avatar size="36" color="red" class="white--text">
          <v-icon small color="white">{{ mdiBedOutline }}</v-icon>
        </v-avatar>
      </v-col>
      <v-col class="pa-3 pl-0 vetalfa-pointer" @click="open">
        <p class="ma-0 subtitle-2 font-weight-bold">
          {{ hospitalization.reason || _getLabel("HOSPITALIZATION") }}
        </p>
        <p class="ma-0 caption grey--text text--darken-1">
          {{ formattedAdmissionDate }}
          <span v-if="isFinished">
            - {{ formattedDischargeDate }}
          </span>
        </p>
        <div class="mt-1">
          <v-chip
            x-small
            :color="isFinished ? 'success' : 'error'"
            text-color="white"
          >
            {{ statusLabel }}
          </v-chip>
          <v-chip
            v-if="hasAddenda"
            x-small
            class="ml-1"
            color="blue lighten-4"
            text-color="blue darken-2"
          >
            {{ _getLabel("WITH_ADDENDUM") }}
          </v-chip>
          <v-chip
            v-if="evolutions.length"
            x-small
            class="ml-1"
            color="blue lighten-4"
            text-color="blue darken-2"
          >
            {{ evolutions.length }}
            {{
              evolutions.length === 1
                ? _getLabel("EVOLUTION_SINGULAR")
                : _getLabel("EVOLUTION_PLURAL")
            }}
          </v-chip>
        </div>
        <p
          v-if="hospitalization.dischargeCondition"
          class="ma-0 caption mt-1"
          style="line-height: 1.3"
        >
          {{ _getLabel("DISCHARGE_CONDITION") }}:
          {{ hospitalization.dischargeCondition }}
        </p>
        <p v-if="isFinished" class="ma-0 caption grey--text text--darken-1 mt-1">
          {{ _getLabel("FINALIZED_BY") }}: {{ signatureText }}
        </p>
      </v-col>
      <v-col cols="auto" class="pr-2 vetalfa-pointer" @click="open">
        <v-icon x-small color="grey">{{ mdiChevronRight }}</v-icon>
      </v-col>
    </v-row>

    <v-divider></v-divider>
    <v-row no-gutters class="px-3 pt-2">
      <v-col cols="12">
        <p class="ma-0 caption font-weight-bold grey--text text--darken-2">
          {{ _getLabel("EVOLUTIONS") }}
        </p>
        <p v-if="!evolutions.length" class="ma-0 caption grey--text text--darken-1">
          {{ _getLabel("NO_EVOLUTIONS") }}
        </p>
        <div
          v-for="(evolution, index) in evolutions"
          :key="evolution.id || `evolution-${index}`"
          class="mt-1"
        >
          <p class="ma-0 caption" style="line-height: 1.3; white-space: pre-wrap">
            {{ evolution.notes }}
          </p>
          <p class="ma-0 caption grey--text text--darken-1">
            {{ getEvolutionSignature(evolution) }}
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="!isFinished" no-gutters class="px-2 py-1" align="center">
      <v-btn
        x-small
        text
        color="primary"
        class="text-none"
        @click.stop="$emit('addEvolution', hospitalization)"
      >
        <v-icon x-small left>{{ mdiPlus }}</v-icon>
        {{ _getLabel("NEW_EVOLUTION") }}
      </v-btn>
      <v-btn
        x-small
        text
        color="primary"
        class="text-none"
        @click.stop="$emit('discharge', hospitalization)"
      >
        <v-icon x-small left>{{ mdiLockOutline }}</v-icon>
        {{ _getLabel("DISCHARGE") }}
      </v-btn>
      <v-btn
        x-small
        text
        color="error"
        class="text-none"
        @click.stop="$emit('remove', hospitalization)"
      >
        <v-icon x-small left>{{ mdiTrashCanOutline }}</v-icon>
        {{ _getLabel("DELETE_RECORD") }}
      </v-btn>
    </v-row>
  </v-card>
</template>

<script>
import {
  mdiChevronRight,
  mdiBedOutline,
  mdiPlus,
  mdiLockOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import hospitalizationsEnum from "@/constants/hospitalizations.enum.js";

export default {
  name: "HospitalizationCard",
  props: {
    hospitalization: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mdiChevronRight,
      mdiBedOutline,
      mdiPlus,
      mdiLockOutline,
      mdiTrashCanOutline,
    };
  },
  computed: {
    formattedAdmissionDate() {
      if (!this.hospitalization.admissionDate && !this.hospitalization.lastUpdate)
        return "-";
      return this._getDateFromTimestamp(
        this.hospitalization.admissionDate || this.hospitalization.lastUpdate
      );
    },
    formattedDischargeDate() {
      if (!this.hospitalization.dischargeDate) return "-";
      return this._getDateFromTimestamp(this.hospitalization.dischargeDate);
    },
    /**
     * A internação fica ativa até a alta: o estado é derivado da assinatura do
     * servidor, não de um campo escolhido no formulário.
     */
    isFinished() {
      return !!(
        this.hospitalization.finalizedAt || this.hospitalization.status >= 1
      );
    },
    statusLabel() {
      return hospitalizationsEnum.getStatusLabel(
        this.isFinished
          ? hospitalizationsEnum.STATUS.DISCHARGED
          : hospitalizationsEnum.STATUS.ADMITTED,
        this._getLabel
      );
    },
    hasAddenda() {
      return ((this.hospitalization.addenda || []).length || 0) > 0;
    },
    evolutions() {
      return [...(this.hospitalization.evolutions || [])].sort(
        (a, b) => (b.createdAt || b.date || 0) - (a.createdAt || a.date || 0)
      );
    },
    signatureText() {
      const author = this.hospitalization.finalizedBy || {};
      const name = [author.displayNamePrefix, author.displayName]
        .filter((part) => !!part)
        .join(" ");
      const crmv =
        author.crmvNumber || author.crmvState
          ? `CRMV ${author.crmvNumber || ""}${
              author.crmvState ? "-" + author.crmvState : ""
            }`.trim()
          : "";
      const date = this.hospitalization.finalizedAt
        ? new Date(this.hospitalization.finalizedAt).toLocaleString("pt-BR")
        : "";
      return [name, crmv, date].filter((part) => !!part).join(" • ") || "-";
    },
  },
  methods: {
    open() {
      this.$emit("open", this.hospitalization);
    },
    getEvolutionSignature(evolution) {
      const author = (evolution && evolution.author) || {};
      const name = [author.displayNamePrefix, author.displayName]
        .filter((part) => !!part)
        .join(" ");
      const timestamp = evolution && (evolution.createdAt || evolution.date);
      const date = timestamp ? new Date(timestamp).toLocaleString("pt-BR") : "";
      return [name, date].filter((part) => !!part).join(" • ");
    },
  },
};
</script>

<style scoped>
.timeline-card {
  transition: box-shadow 0.2s;
}
.timeline-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
</style>
